import type { MouseEvent } from "react";

import styled from "styled-components";

import { SparklineCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import {
    ChangeChip,
    Dropdown,
    IconButton,
    EllipsisDropdownItem,
    PixelPlaceholderLoader,
    Tooltip,
} from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { formatPercent } from "app/pages/market-research/utils/numberFormatters";

import { TrackSectionEntityCardContainer } from "./TrackSectionEntityCardContainer";
import type { TrendData } from "./useKeywordListsTrendsData";

const ChipSkeleton = styled(PixelPlaceholderLoader)`
    border-radius: 999px;
`;

const SparklineSkeleton = styled(PixelPlaceholderLoader)`
    border-radius: 2px;
`;

const StyledEllipsisDropdownItem = styled(EllipsisDropdownItem)`
    svg {
        height: 100%;
        width: 100%;
        fill: ${colorsPalettes.carbon[200]};
    }
`;

const colors = {
    title: colorsPalettes.carbon[500],
    subTitle: colorsPalettes.carbon[300],
};

interface TrackSectionEntityCardProps {
    isEditable: boolean;
    entity: IUserKeywordGroup;
    isTrendLoading?: boolean;
    trendData?: TrendData[];
    onEntityClick: (item: IUserKeywordGroup) => void;
    onEntityEditClick: (item: IUserKeywordGroup) => void;
    onEntityShareClick: (item: IUserKeywordGroup) => void;
    onEntityDeleteClick: (item: IUserKeywordGroup) => void;
}

const calculateTrendChange = (trendData?: TrendData[]): number | undefined => {
    if (!trendData || trendData.length === 0) {
        return undefined;
    }

    const firstPoint = trendData[0]?.currentPeriod?.volume ?? 0;
    const lastPoint = trendData[trendData.length - 1]?.currentPeriod?.volume ?? 0;

    if (firstPoint === 0) {
        return undefined;
    }

    return (lastPoint - firstPoint) / firstPoint;
};

const getTrendSentiment = (change: number): "positive" | "negative" | "neutral" => {
    if (change > 0) {
        return "positive";
    }
    if (change < 0) {
        return "negative";
    }
    return "neutral";
};

const getSparklineData = (trendData?: TrendData[]): number[] => {
    if (!trendData) {
        return [];
    }
    return trendData.map((point) => point.currentPeriod?.volume ?? 0);
};

export const TrackSectionEntityCard = ({
    isEditable,
    entity,
    isTrendLoading,
    trendData,
    onEntityClick,
    onEntityEditClick,
    onEntityShareClick,
    onEntityDeleteClick,
}: TrackSectionEntityCardProps) => {
    const t = useTranslation();
    const trendChange = calculateTrendChange(trendData);

    const handleClick = (e: MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest(".menu-button")) {
            return;
        }
        e.preventDefault();
        onEntityClick(entity);
    };

    const handleDropdownItemClick = ({ id }: { id: string }) => {
        switch (id) {
            case "edit":
                onEntityEditClick(entity);
                break;
            case "share":
                onEntityShareClick(entity);
                break;
            case "delete":
                onEntityDeleteClick(entity);
                break;
        }
    };

    return (
        <TrackSectionEntityCardContainer onClick={handleClick}>
            <TrackSectionEntityCardContainer.IconBox />
            <TrackSectionEntityCardContainer.TitleGroup>
                <Text
                    fontVariant="subtitle1"
                    color={colors.title}
                >
                    {entity.Name}
                </Text>
                <Text
                    fontVariant="listItem3"
                    color={colors.subTitle}
                >
                    {t("demand.analysis.home.library.list_item.subtitle", {
                        count: entity.Keywords.length,
                    })}
                </Text>
            </TrackSectionEntityCardContainer.TitleGroup>
            {isTrendLoading ? (
                <TrackSectionEntityCardContainer.TrendGroup>
                    <ChipSkeleton
                        width={64}
                        height={24}
                    />
                    <SparklineSkeleton
                        width={96}
                        height={32}
                    />
                </TrackSectionEntityCardContainer.TrendGroup>
            ) : trendChange !== undefined ? (
                <TrackSectionEntityCardContainer.TrendGroup>
                    <Tooltip content={t("demand_analysis.home.library.list_item.trend.tooltip")}>
                        <span>
                            <ChangeChip
                                sentiment={getTrendSentiment(trendChange)}
                                size="small"
                            >
                                {formatPercent(Math.abs(trendChange), 1)}
                            </ChangeChip>
                        </span>
                    </Tooltip>
                    <SparklineCell
                        data={getSparklineData(trendData)}
                        type="area"
                        width={96}
                        height={28}
                    />
                </TrackSectionEntityCardContainer.TrendGroup>
            ) : null}
            {isEditable ? (
                <Tooltip content={t("demand_analysis.home.library.list_item.actions.tooltip")}>
                    <span>
                        <Dropdown
                            width={240}
                            dropdownPopupPlacement="bottom-right"
                            onClick={handleDropdownItemClick}
                        >
                            <IconButton
                                className="menu-button"
                                iconName="dots-more"
                                iconSize="sm"
                                type="flat"
                            />
                            <StyledEllipsisDropdownItem
                                id="edit"
                                iconName="edit-icon"
                            >
                                {t("demand_analysis.home.library.list_item.actions.edit")}
                            </StyledEllipsisDropdownItem>
                            <StyledEllipsisDropdownItem
                                id="share"
                                iconName="social-share"
                            >
                                {t("demand_analysis.home.library.list_item.actions.share")}
                            </StyledEllipsisDropdownItem>
                            <StyledEllipsisDropdownItem
                                id="delete"
                                iconName="delete"
                            >
                                {t("demand_analysis.home.library.list_item.actions.remove")}
                            </StyledEllipsisDropdownItem>
                        </Dropdown>
                    </span>
                </Tooltip>
            ) : null}
        </TrackSectionEntityCardContainer>
    );
};
